import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useBanner } from '@xingternal/banner';
import { useViewerData } from '@xingternal/hub';

import { trackFormError } from '@/domain/job-application/helpers';
import { trackNwtEvent } from '@/helpers/nwt-tracking';
import { useBreakpoints } from '@/hooks';

export type ApplyFormSubmitError = 'generic' | 'deactivatedJob' | 'noXingApply' | 'badRequest';

const submitErrorCopyKeys: Record<ApplyFormSubmitError, string> = {
  generic: 'ERROR_SOMETHING_WENT_WRONG',
  deactivatedJob: 'JOBS_POSTING_XING_APPLY_DEACTIVATED_ERROR',
  noXingApply: 'JOBS_POSTING_XING_APPLY_TYPE_ERROR',
  badRequest: '',
};

const submitErrorTrackActions: Record<ApplyFormSubmitError, string> = {
  generic: 'jobs_submit_error_network_issue',
  deactivatedJob: 'jobs_submit_error_deactivated_job',
  noXingApply: 'jobs_submit_error_no_xing_application',
  badRequest: 'jobs_submit_error_try_again',
};

const submitErrorNWTContexts: Record<ApplyFormSubmitError, string> = {
  generic: 'instant_apply_submit_error_network_issue',
  deactivatedJob: 'instant_apply_submit_error_deactivated_job',
  noXingApply: 'instant_apply_submit_error_no_xing_application',
  badRequest: 'instant_apply_submit_error_try_again',
};

type ApplyFormSubmitErrorsValue = {
  showSubmitError: (error: ApplyFormSubmitError) => void;
};

export const useApplyFormSubmitErrors = (jobGlobalId: string): ApplyFormSubmitErrorsValue => {
  const { showBanner } = useBanner();
  const intl = useIntl();
  const { data } = useViewerData();
  const userId = data?.user?.id;
  const { isMobile } = useBreakpoints();

  const showSubmitError = useCallback(
    (error: ApplyFormSubmitError) => {
      submitErrorCopyKeys[error] &&
        showBanner({
          children: intl.formatMessage({ id: submitErrorCopyKeys[error] }),
          variant: 'error',
        });
      trackFormError(submitErrorTrackActions[error]);
      trackNwtEvent({
        context: submitErrorNWTContexts[error],
        event: 'error_shown',
        item_urn: jobGlobalId,
        page: 'jobs/apply',
        channel: isMobile ? 'webm' : 'web',
        user_id: userId,
      });
    },
    [intl, isMobile, jobGlobalId, showBanner, userId]
  );

  return {
    showSubmitError,
  };
};
